import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

interface YearSelectorProps {
    selectedYear: number;
    onYearChange: (year: number) => void;
    years: number[];
    disabled?: boolean;
}

export default function YearSelector({ selectedYear, onYearChange, years, disabled = false }: YearSelectorProps) {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                type="button"
                onClick={() => !disabled && setIsOpen(!isOpen)}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-2 rounded-lg
                    ${disabled 
                        ? 'bg-gray-50 text-gray-400 cursor-not-allowed border-gray-200' 
                        : 'bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-300 cursor-pointer border-gray-300'
                    } 
                    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200`}
                disabled={disabled}
            >
                <span className="min-w-[3.5rem] text-center">{selectedYear}</span>
                <ChevronDown 
                    size={16} 
                    className={`transition-transform duration-200 text-gray-500 ${isOpen ? 'rotate-180' : ''}`} 
                />
            </button>

            <div 
                className={`absolute left-0 mt-2 w-28 bg-white rounded-lg shadow-xl border-2 border-gray-200 z-10
                    transform transition-all duration-200 ease-out origin-top
                    ${isOpen 
                        ? 'opacity-100 scale-100 translate-y-0' 
                        : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
                    }`}
            >
                <div className="py-1.5">
                    {years.map((year) => (
                        <button
                            key={year}
                            onClick={() => {
                                onYearChange(year);
                                setIsOpen(false);
                            }}
                            className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors duration-150
                                ${year === selectedYear 
                                    ? 'bg-blue-50 text-blue-700' 
                                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                }`}
                        >
                            <div className="flex items-center justify-between">
                                <span>{year}</span>
                                {year === selectedYear && (
                                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                                )}
                            </div>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
} 